import { useState, useCallback, useMemo } from 'react';

/**
 * Custom hook for managing notification UI state
 *
 * Manages:
 * - Expand/collapse states for sections
 * - Loading states for UI interactions
 * - Error processing statuses configuration
 * - Default expanded state management
 *
 * Similar to useUploadUI but for notification context
 */
export function useNotificationUI() {
  // Expand/collapse state management
  const [isExpandAll, setIsExpandAll] = useState<boolean>(true);
  const [isCollapseAll, setIsCollapseAll] = useState<boolean>(false);

  // Loading states
  const [isLoading, setIsLoading] = useState<boolean>(false);

  // Error processing statuses (can be configured per notification)
  const [errorProcessingStatuses, setErrorProcessingStatuses] = useState<string[]>([]);

  // Determine the current expanded state - expand takes priority over collapse
  const defaultExpanded = useMemo(() => {
    if (isExpandAll) return true;
    if (isCollapseAll) return false;
    return true; // Default fallback
  }, [isExpandAll, isCollapseAll]);

  // Actions for expand/collapse
  const handleExpandAll = useCallback(() => {
    setIsExpandAll(true);
    setIsCollapseAll(false);
  }, []);

  const handleCollapseAll = useCallback(() => {
    setIsExpandAll(false);
    setIsCollapseAll(true);
  }, []);

  const resetExpandState = useCallback(() => {
    setIsExpandAll(true);
    setIsCollapseAll(false);
  }, []);

  // Loading state actions
  const setLoadingState = useCallback((loading: boolean) => {
    setIsLoading(loading);
  }, []);

  // Error processing statuses actions
  const updateErrorProcessingStatuses = useCallback((statuses: string[]) => {
    setErrorProcessingStatuses(statuses);
  }, []);

  const addErrorProcessingStatus = useCallback((status: string) => {
    setErrorProcessingStatuses((prev) => (prev.includes(status) ? prev : [...prev, status]));
  }, []);

  const removeErrorProcessingStatus = useCallback((status: string) => {
    setErrorProcessingStatuses((prev) => prev.filter((s) => s !== status));
  }, []);

  // Reset all UI state
  const resetUI = useCallback(() => {
    setIsExpandAll(true);
    setIsCollapseAll(false);
    setIsLoading(false);
    setErrorProcessingStatuses([]);
  }, []);

  return {
    // Expand/collapse state
    isExpandAll,
    isCollapseAll,
    defaultExpanded,

    // Loading state
    isLoading,

    // Error processing statuses
    errorProcessingStatuses,

    // Expand/collapse actions
    handleExpandAll,
    handleCollapseAll,
    resetExpandState,

    // Loading actions
    setLoadingState,

    // Error processing actions
    updateErrorProcessingStatuses,
    addErrorProcessingStatus,
    removeErrorProcessingStatus,

    // Reset action
    resetUI,

    // Computed states
    hasExpandedSections: isExpandAll,
    hasCollapsedSections: isCollapseAll,
    canExpand: !isExpandAll,
    canCollapse: !isCollapseAll,
  };
}
